import { ActionDispatching, CaseData, CompositeKeys, ConstraintResponseType } from 'app/types-shared';
import { ComplexValue } from 'ui';
import { createUTCDate, toISODatewithoutTimezone } from 'app/infrastructure/data/date';
import { caseTypeId as getCaseTypeId, caseContextId as getCaseContextId } from 'app/infrastructure/data/wireformat';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { LoggedError } from 'app/infrastructure/redux';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { ReceiveCase } from 'app/area/case/features/connected-cases/action-types';
import { api as callsToActionsApi } from 'app/area/case/features/calls-to-actions/api';
import { api } from './api';
import { addEntry, egressEntry, entryChange, EntryChange, findOpenEntryPath } from './case-data';

const toasts = toastFactory();

const nowAsWireDate = () => toISODatewithoutTimezone(createUTCDate())();

const toRecordedBy = (user?: ComplexValue<number>) =>
  user ? { value: user.value, description: user.description } : undefined;

const receiveCase = async (dispatch: any, caseId: number, caseData: CaseData) => {
  const caseType = getCaseTypeId(caseData);
  const uiDefinition = await dispatch(
    responseCachingActionCreators.getUiDefinition(caseType, getCaseContextId(caseData))
  );
  if (uiDefinition) {
    dispatch(new ReceiveCase(caseId, caseData, { ...uiDefinition, applicationId: caseType }));
  }
};

const handleSaveError = (err: any, dispatch: any) => {
  const errors = err?.validationErrors?.filter(e => e.responseType === ConstraintResponseType.Error);
  if (errors?.length) {
    toasts.dispatchError(errors.map(e => e.message).join('\n'));
  } else if (err?.userMessages) {
    dispatch(new ErrorNotification(err.userMessages));
  } else {
    dispatch(new LoggedError(err));
  }
};

interface UpdateTarget {
  caseId: number;
  involvedPersonPath: CompositeKeys;
}

/**
 * Fetches the case, applies the change to it and saves it, the same way a status change is saved in read mode.
 * The store is updated with the resulting case, also when the case turned out to be out of date.
 * Resolves `{ saved: true, openEntryPath }` when the change was saved. `openEntryPath` is the entry
 * the person is inside of in the saved case, undefined when the person is not inside.
 */
const updateCase = (
  { caseId, involvedPersonPath }: UpdateTarget, change: (caseData: CaseData) => EntryChange, updateCaseStore = true
): ActionDispatching<any, any> =>
  async (dispatch: any) => {
    try {
      const caseResponse = await callsToActionsApi.case(caseId);
      const caseData = caseResponse.json.data;
      const outcome = change(caseData);
      let openEntryPath: CompositeKeys | undefined;

      if (outcome === entryChange.changed) {
        await api.saveCase(caseData, caseResponse.etag);
        const savedCase = await callsToActionsApi.case(caseId);
        openEntryPath = findOpenEntryPath(savedCase.json.data, involvedPersonPath);
        if (updateCaseStore) {
          await receiveCase(dispatch, caseId, savedCase.json.data);
        }
      } else if (updateCaseStore) {
        await receiveCase(dispatch, caseId, caseData);
      }

      if (outcome === entryChange.alreadyInside) {
        return { alreadyInside: true };
      }
      return outcome === entryChange.changed ? { saved: true, openEntryPath } : null;
    } catch (err) {
      handleSaveError(err, dispatch);
      return null;
    }
  };

export const caseActionCreators = {

  /**
   * Adds an entry for an involved person of a case that is not being edited.
   * Resolves `{ alreadyInside: true }` when the person already has an entry without egress.
   */
  onAddInvolvedPersonEntry: (
    caseId: number, involvedPersonPath: CompositeKeys, currentUser?: ComplexValue<number>, updateCaseStore = true
  ) =>
    updateCase({ caseId, involvedPersonPath }, caseData =>
      addEntry(caseData, involvedPersonPath, { entryTime: nowAsWireDate(), recordedBy: toRecordedBy(currentUser) }), updateCaseStore),

  /**
   * Egresses an entry of a case that is not being edited.
   */
  onEgressInvolvedPersonEntry: (
    caseId: number, entryPath: CompositeKeys, currentUser?: ComplexValue<number>, updateCaseStore = true
  ) =>
    updateCase({ caseId, involvedPersonPath: entryPath.slice(0, -1) }, caseData =>
      egressEntry(caseData, entryPath, { egressTime: nowAsWireDate(), recordedBy: toRecordedBy(currentUser) }), updateCaseStore)
};
